import React, { useState, useEffect } from 'react';
import { legalApi } from '../services/api';
import { Button, Spinner } from '../components/UI';
import { Shield, CheckSquare, Square } from '@phosphor-icons/react';

export default function LegalAcceptanceModal({ onAccepted }) {
  const [terms, setTerms] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeView, setActiveView] = useState('terms');

  useEffect(() => {
    Promise.all([
      legalApi.getPage('terms'),
      legalApi.getPage('privacy'),
    ]).then(([t, p]) => {
      setTerms(t.data);
      setPrivacy(p.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await legalApi.accept();
      onAccepted();
    } catch {
      alert('Failed to record acceptance');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const activePage = activeView === 'terms' ? terms : privacy;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4" data-testid="legal-acceptance-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-[#E8E8ED]">
          <div className="w-10 h-10 rounded-xl bg-[#6B7280]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#6B7280]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#1A1F36]">Legal Agreement Required</h2>
            <p className="text-xs text-[#888]">Please review and accept our terms to continue</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E8E8ED]">
          <button
            onClick={() => setActiveView('terms')}
            className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors ${
              activeView === 'terms' ? 'text-[#1A1F36] border-b-2 border-[#1A1F36]' : 'text-[#888] hover:text-[#555]'
            }`}
            data-testid="tab-terms"
          >
            Terms of Use {terms && `(v${terms.version})`}
          </button>
          <button
            onClick={() => setActiveView('privacy')}
            className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors ${
              activeView === 'privacy' ? 'text-[#1A1F36] border-b-2 border-[#1A1F36]' : 'text-[#888] hover:text-[#555]'
            }`}
            data-testid="tab-privacy"
          >
            Privacy Policy {privacy && `(v${privacy.version})`}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5" data-testid="legal-scroll-area">
          {activePage && (
            <div className="prose prose-sm max-w-none text-[#333] leading-relaxed whitespace-pre-wrap text-sm">
              {activePage.content}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#E8E8ED] space-y-3">
          <button
            onClick={() => setChecked(!checked)}
            className="flex items-start gap-2.5 group w-full text-left"
            data-testid="accept-checkbox"
          >
            {checked ? (
              <CheckSquare className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" weight="fill" />
            ) : (
              <Square className="w-5 h-5 text-[#CCC] group-hover:text-[#888] shrink-0 mt-0.5" />
            )}
            <span className="text-sm text-[#333]">
              I have read and agree to the <strong>Terms of Use</strong> and <strong>Privacy Policy</strong>
            </span>
          </button>

          <Button
            className="w-full"
            disabled={!checked || submitting}
            onClick={handleAccept}
            style={{ backgroundColor: '#1A1F36' }}
            data-testid="accept-btn"
          >
            {submitting ? 'Processing...' : 'Accept & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
